import { useEffect, useState } from "react";
import {
  FaExclamationTriangle,
  FaPen,
  FaPlus,
  FaTrash,
} from "react-icons/fa";
import { OverlayTrigger, Popover, Tooltip } from "react-bootstrap";
import { Table } from "containers/Table";
import { IColum } from "containers/Table/interfaces";
import { formatarDinheiro } from "_utils/Money";
import { ICustoEmLote, IProcessoEmLote } from "../../../interface/IProcessoEmLote";
import { ModalEditarCustos } from "./ModalEditarCustos";
import { ModalCertezaRemoverProcesso } from "./ModalCertezaRemoverProcesso";
import { ModalSucessoRemoverProcesso } from "./ModalSucessoRemoverProcesso";
import {
  AlertaAReceber,
  AlertaValor,
  BarraPesquisa,
  BotoesAcoes,
  Button,
  Container,
  CustosAdicionados,
  Header,
  InputPesquisa,
  ServicosCelula,
  TagCusto,
  TagCustoExtra,
} from "./style";

interface IProps {
  processos: IProcessoEmLote[];
  setProcessos: React.Dispatch<React.SetStateAction<IProcessoEmLote[]>>;
  selectedRows: IProcessoEmLote[];
  setSelectedRows: (rows: IProcessoEmLote[]) => void;
  clearRows: boolean;
  totalCustos: number;
  valorDivergente: boolean;
  onRemoverProcessos: () => void;
  onAdicionarCusto: () => void;
  onSelecionarProcessos: () => void;
  onClickProcesso: (idProcesso: number) => void;
}

function CustosCelula({ custos }: { custos: ICustoEmLote[] }) {
  const MAX = 1;
  const visiveis = custos.slice(0, MAX);
  const extras = custos.length - MAX;

  const popover = (
    <Popover id="popover-custos" style={{ minWidth: 200 }}>
      <Popover.Body style={{ padding: "12px 16px" }}>
        <div style={{ border: "1px solid #dee2e6", borderRadius: 4, overflow: "hidden" }}>
          {custos.map((c, i) => (
            <div
              key={i}
              style={{
                padding: "6px 12px",
                fontSize: "0.82rem",
                borderBottom: i < custos.length - 1 ? "1px solid #f0f0f0" : "none",
              }}
            >
              {c.label}
            </div>
          ))}
        </div>
      </Popover.Body>
    </Popover>
  );

  return (
    <ServicosCelula>
      {visiveis.map((c, i) => (
        <OverlayTrigger key={i} placement="top" overlay={<Tooltip>{c.label}</Tooltip>}>
          <TagCusto>{c.label}</TagCusto>
        </OverlayTrigger>
      ))}
      {extras > 0 && (
        <OverlayTrigger placement="top" trigger={["hover", "focus"]} overlay={popover}>
          <TagCustoExtra>+{extras}</TagCustoExtra>
        </OverlayTrigger>
      )}
    </ServicosCelula>
  );
}

export function ListaProcessosLote({
  processos,
  setProcessos,
  selectedRows,
  setSelectedRows,
  clearRows,
  totalCustos,
  valorDivergente,
  onRemoverProcessos,
  onAdicionarCusto,
  onSelecionarProcessos,
  onClickProcesso,
}: IProps) {
  const [textoBusca, setTextoBusca] = useState("");
  const [dadosFiltrados, setDadosFiltrados] = useState<IProcessoEmLote[]>([]);
  const [showModalEditar, setShowModalEditar] = useState(false);
  const [showModalRemover, setShowModalRemover] = useState(false);
  const [showModalSucessoRemover, setShowModalSucessoRemover] = useState(false);
  const [processoEditar, setProcessoEditar] = useState<IProcessoEmLote | null>(null);

  useEffect(() => {
    const busca = textoBusca.toLowerCase().replace("-", "");
    if (!busca) {
      setDadosFiltrados(processos);
      return;
    }
    setDadosFiltrados(
      processos.filter(
        (p) =>
          String(p.idProcesso).includes(busca) ||
          (p.placa && p.placa.replace("-", "").toLowerCase().includes(busca)),
      ),
    );
  }, [textoBusca, processos]);

  const onSalvarEdicao = (processoAtualizado: IProcessoEmLote) => {
    setProcessos((atual) =>
      atual
        .map((p) => (p.idProcesso === processoAtualizado.idProcesso ? processoAtualizado : p))
        .filter((p) => p.custos.length > 0),
    );
    setShowModalEditar(false);
    setProcessoEditar(null);
  };

  const onRemoverCusto = (idDescricao: number) => {
    if (!processoEditar) return;
    setProcessos((atual) =>
      atual
        .map((p) =>
          p.idProcesso === processoEditar.idProcesso
            ? { ...p, custos: p.custos.filter((c) => c.idDescricao !== idDescricao) }
            : p,
        )
        .filter((p) => p.custos.length > 0),
    );
  };

  const columns: IColum<IProcessoEmLote>[] = [
    {
      name: "Processo",
      selector: ({ idProcesso }) => (
        <span
          className="link-primary"
          style={{ cursor: "pointer" }}
          onClick={() => onClickProcesso(idProcesso)}
        >
          {idProcesso}
        </span>
      ),
      width: "100px",
    },
    {
      name: "Placa",
      selector: ({ placa }) => placa ?? "--",
      width: "110px",
    },
    {
      name: "Renavam",
      selector: ({ renavam }) => renavam ?? "--",
      width: "130px",
    },
    {
      name: "Chassi",
      selector: ({ chassi }) => chassi ?? "--",
      width: "180px",
    },
    {
      name: "Empresa",
      selector: ({ nomeEmpresa }) => nomeEmpresa ?? "--",
      width: "180px",
    },
    {
      name: "Serviço",
      selector: ({ servico }) => servico ?? "--",
      width: "160px",
    },
    {
      name: "Custos lançados",
      selector: ({ custos }) => <CustosCelula custos={custos} />,
      width: "210px",
    },
    {
      name: "Custos",
      selector: ({ custos }) =>
        custos.length
          ? formatarDinheiro(custos.reduce((acc: number, c: ICustoEmLote) => acc + Number(c.valor || 0), 0))
          : "--",
    },
    {
      name: (
        <OverlayTrigger placement="top" overlay={<Tooltip>Serviços a receber</Tooltip>}>
          <span>A receber</span>
        </OverlayTrigger>
      ),
      selector: ({ aReceber, custos }) => {
        const totalCustoProcesso = custos.reduce((acc: number, c: ICustoEmLote) => acc + Number(c.valor || 0), 0);
        const alerta =
          totalCustoProcesso > 0 && (aReceber === null || aReceber < totalCustoProcesso);
        return (
          <div style={{ display: "flex", alignItems: "center" }}>
            <span>{formatarDinheiro(aReceber ?? 0)}</span>
            {alerta && (
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip>
                    <FaExclamationTriangle style={{ marginRight: 4 }} />
                    Valor a receber inferior ao custo do processo.
                  </Tooltip>
                }
              >
                <AlertaAReceber>
                  <FaExclamationTriangle />
                </AlertaAReceber>
              </OverlayTrigger>
            )}
          </div>
        );
      },
      width: "130px",
    },
    {
      name: "Ações",
      selector: (processo) => (
        <OverlayTrigger placement="top" overlay={<Tooltip>Editar custos</Tooltip>}>
          <span
            style={{ cursor: "pointer" }}
            onClick={() => {
              setProcessoEditar(processo);
              setShowModalEditar(true);
            }}
          >
            <FaPen />
          </span>
        </OverlayTrigger>
      ),
      width: "60px",
    },
  ];

  return (
    <Container>
      <Header>
        <CustosAdicionados>
          Custos adicionados{" "}
          <strong>{totalCustos > 0 ? formatarDinheiro(totalCustos) : "R$ 0,00"}</strong>
          {valorDivergente && (
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip>Soma dos custos adicionados diverge do valor total do Recibo/NF</Tooltip>
              }
            >
              <AlertaValor>
                <FaExclamationTriangle />
              </AlertaValor>
            </OverlayTrigger>
          )}
        </CustosAdicionados>
        <BotoesAcoes>
          <BarraPesquisa>
            <InputPesquisa
              placeholder="ID Processo ou placa"
              value={textoBusca}
              onChange={(e) => setTextoBusca(e.target.value)}
            />
          </BarraPesquisa>
          <Button
            outline
            variante="primary"
            disabled={!selectedRows.length}
            onClick={() => setShowModalRemover(true)}
          >
            <FaTrash /> Remover do lote
          </Button>
          <Button
            outline
            variante="primary"
            disabled={!selectedRows.length}
            onClick={onAdicionarCusto}
          >
            <FaPlus /> Adicionar custo
          </Button>
        </BotoesAcoes>
      </Header>
      <hr style={{ marginTop: "0.75rem" }} />
      <Table
        keyField="idProcesso"
        columns={columns}
        data={dadosFiltrados}
        loading={false}
        noSubHeader
        possuiTooltip={false}
        selectableRows
        setRowsSelected={setSelectedRows}
        headerFixo
        clearSelectedRows={clearRows}
      />

      {processoEditar && (
        <ModalEditarCustos
          show={showModalEditar}
          handleClose={() => {
            setShowModalEditar(false);
            setProcessoEditar(null);
          }}
          processo={processoEditar}
          onSalvar={onSalvarEdicao}
          onRemoverCusto={onRemoverCusto}
        />
      )}

      <ModalCertezaRemoverProcesso
        show={showModalRemover}
        handleClose={() => setShowModalRemover(false)}
        onConfirm={() => {
          onRemoverProcessos();
          setShowModalRemover(false);
          setShowModalSucessoRemover(true);
        }}
      />

      <ModalSucessoRemoverProcesso
        show={showModalSucessoRemover}
        handleClose={() => setShowModalSucessoRemover(false)}
      />
    </Container>
  );
}
